import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {combineLatest, NEVER, Observable, of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, map, switchMap} from 'rxjs/operators';
import {UserRole} from '../../enums/user-role';
import {AppConfigService} from '../../services/config.service';
import {GlobalSettingsApiService} from '../../services/global-settings-api';
import {SettingsApiService} from '../../services/settings-api.service';
import {UserService} from '../../services/user.service';
import {Watchlist} from './watchlist';
import {WatchlistDefinition} from './watchlist-definitnion';
import {WatchlistFactory} from './watchlist.factory';

export enum WatchlistActionType {
    WATCHLIST_NEW,
    WATCHLIST_DELETE,
    WATCHLIST_CHANGE
}

export interface WatchlistUpdate {
    type: WatchlistActionType;
    watchlist: Watchlist;
}

const SETTINGS_API_GLOBAL_WATCHLIST_KEY = 'watchlists';
const SETTINGS_API_USER_WATCHLIST_KEY = 'user-watchlists';

@Injectable()
export class WatchlistService {

    public watchlists: Watchlist[] = [];

    public readonly updateWatchlists$: Observable<WatchlistUpdate>;

    private readonly updateWatchlistsSubject$: Subject<WatchlistUpdate> = new Subject();

    private readonly settingsApiGlobalWatchlistsKey: string = SETTINGS_API_GLOBAL_WATCHLIST_KEY;

    private readonly settingsApiUserWatchlistsKey: string = SETTINGS_API_USER_WATCHLIST_KEY;

    constructor(
        private appConfigService: AppConfigService,
        private settingsApiService: SettingsApiService,
        private globalSettingsApiService: GlobalSettingsApiService,
        private userService: UserService
    ) {
        this.updateWatchlists$ = this.updateWatchlistsSubject$.asObservable();
        this.storeWatchlistsOnUpdate();
    }

    public update(watchlistUpdate: WatchlistUpdate): void {
        this.updateWatchlistsSubject$.next(watchlistUpdate);
    }

    public deleteWatchlist(watchlist: Watchlist): void {

        // Don't allow to delete the last watchlist
        if (this.watchlists.length > 1) {
            const removedItems = _.remove(this.watchlists, item => item === watchlist);

            if (removedItems.length > 0) {
                this.update({
                    type: WatchlistActionType.WATCHLIST_DELETE,
                    watchlist
                });
            }
        } else {
            console.warn('Attempt to remove the last watchlist. Ignored. There should be at least one watchlist.');
        }
    }

    public addWatchlist(watchlist: Watchlist): void {
        this.watchlists.unshift(watchlist);
        this.update({
            type: WatchlistActionType.WATCHLIST_NEW,
            watchlist
        });
    }

    public getDefaultWatchlist(): Watchlist {
        const demoWatchlist = this.watchlists.find(watchlist =>
            watchlist.id === this.appConfigService.appConfig.defaultWatchlistId
        );

        return demoWatchlist || this.watchlists[0];
    }

    public findWatchlistById(id: string): Watchlist {
        return _.find(this.watchlists, {id});
    }

    public getUserWatchlists(): Watchlist[] {
        return this.watchlists.filter(watchlist => !watchlist.isReadonly);
    }

    public fetchWatchlistsFromServer(): Observable<Watchlist[]> {
        const noAuth = !this.userService.user;

        return combineLatest([
            this.fetchPredefinedWatchlistsFromServer(),
            !noAuth ? this.fetchUserWatchlistsFromServer() : of([])
        ]).pipe(
            map(([predefined, customs]) => {
                const allWatchlists: Watchlist[] = [
                    ...predefined,
                    ...customs
                ];

                /**
                 * There should be at least one watchlist,
                 * otherwise add an empty watchlist.
                 */
                if (!allWatchlists.length) {
                    allWatchlists.push(
                        WatchlistFactory.getDefaultEmptyWatchlist()
                    );
                }

                this.setWatchlists(allWatchlists);

                return this.watchlists;
            })
        );
    }

    private fetchUserWatchlistsFromServer(): Observable<WatchlistDefinition[]> {
        return this.settingsApiService.fetch<WatchlistDefinition[]>(this.settingsApiUserWatchlistsKey).pipe(
            map(watchlistDefs => WatchlistFactory.createWatchlistsByDefinition(watchlistDefs)),
            catchError(() => of([]))
        );
    }

    private fetchPredefinedWatchlistsFromServer(): Observable<WatchlistDefinition[]> {
        return this.globalSettingsApiService.fetch<WatchlistDefinition[]>(this.settingsApiGlobalWatchlistsKey).pipe(
            map(watchlistDefs =>
                WatchlistFactory.createWatchlistsByDefinition(watchlistDefs)
                    .map(WatchlistFactory.makeWatchlistReadonly)
            ),
            catchError(() => of([]))
        );
    }

    private storeUserWatchlistsOnServer(): Observable<unknown> {
        const watchlistDefs = this.getUserWatchlists()
            .map(watchlist => WatchlistFactory.getWatchlistDefinition(watchlist));

        return this.settingsApiService.store(this.settingsApiUserWatchlistsKey, watchlistDefs);
    }

    private setWatchlists(watchlists: Watchlist[]): void {
        this.watchlists = watchlists;
    }

    private storeWatchlistsOnUpdate(): void {
        const CHANGES_DEBOUNCE_TIME = 100;

        this.userService.user$.pipe(
            distinctUntilChanged(),
            switchMap(user => user
                ? this.updateWatchlists$.pipe(
                    debounceTime(CHANGES_DEBOUNCE_TIME),
                    switchMap(() => this.storeUserWatchlistsOnServer()),
                    switchMap(() => user.role === UserRole.ADMIN
                        ? this.storeGlobalWatchlistsOnServer()
                        : of(null)
                    )
                )
                : NEVER
            )
        ).subscribe();
    }

    private storeGlobalWatchlistsOnServer(): Observable<unknown> {
        const watchlistDefs = this.getGlobalWatchlists()
            .map(watchlist => WatchlistFactory.getWatchlistDefinition(watchlist));

        return this.globalSettingsApiService.store(this.settingsApiGlobalWatchlistsKey, watchlistDefs);
    }

    private getGlobalWatchlists(): Watchlist[] {
        return this.watchlists.filter(watchlist => watchlist.isReadonly);
    }

}
